import { useState, useEffect, useCallback } from 'react';
import MyApiClient from '../api-client/MyApiClient';

// Cache globale per il profilo
let profileCache = {
  data: null,
  timestamp: null,
  isLoading: false
};

// Durata del cache in millisecondi (5 minuti)
const CACHE_DURATION = 5 * 60 * 1000;

// Lista di listeners per aggiornamenti del profilo
let profileListeners = new Set();

// Funzione per notificare tutti i listeners
const notifyListeners = (profile) => {
  profileListeners.forEach(listener => listener(profile));
};

// Hook personalizzato per gestire il profilo utente
export const useProfile = (options = {}) => {
  const { autoFetch = true, includeTitles = false } = options;
  const [profile, setProfile] = useState(profileCache.data);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Funzione per verificare se il cache è valido
  const isCacheValid = useCallback(() => {
    if (!profileCache.data || !profileCache.timestamp) return false;
    return Date.now() - profileCache.timestamp < CACHE_DURATION;
  }, []);

  // Funzione per ottenere il token
  const getToken = useCallback(() => {
    const tokenDetailsString = sessionStorage.getItem('userDetails');
    if (tokenDetailsString) {
      const tokenDetails = JSON.parse(tokenDetailsString);
      return tokenDetails.idToken;
    }
    return null;
  }, []);

  // Funzione per fetchare il profilo
  const fetchProfile = useCallback(async (force = false) => {
    const token = getToken();
    if (!token) {
      setError('Token non disponibile');
      return null;
    }

    // Se il cache è valido e non è forzato, restituisci i dati cached
    if (!force && isCacheValid()) {
      setProfile(profileCache.data);
      return profileCache.data;
    }

    // Evita chiamate multiple simultanee
    if (profileCache.isLoading) {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!profileCache.isLoading) {
            resolve(profileCache.data);
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    try {
      profileCache.isLoading = true;
      setLoading(true);
      setError(null);

      const apiClient = new MyApiClient();
      const promises = [apiClient.getProfile(token)];
      
      if (includeTitles) {
        promises.push(apiClient.getTitles(token));
      }

      const results = await Promise.all(promises);
      const profileData = results[0];
      
      if (includeTitles && results[1]) {
        profileData.club.titles = results[1];
      }

      // Aggiorna il cache
      profileCache.data = profileData;
      profileCache.timestamp = Date.now();
      
      setProfile(profileData);
      notifyListeners(profileData);
      
      return profileData;
    } catch (err) {
      console.error('Errore nel caricamento del profilo:', err);
      setError(err.message || 'Errore nel caricamento del profilo');
      return null;
    } finally {
      profileCache.isLoading = false;
      setLoading(false);
    }
  }, [getToken, isCacheValid, includeTitles]);

  // Funzione per invalidare il cache
  const invalidateCache = useCallback(() => {
    profileCache.data = null;
    profileCache.timestamp = null;
  }, []);

  // Funzione per aggiornare il profilo
  const updateProfile = useCallback((updatedProfile) => {
    profileCache.data = updatedProfile;
    profileCache.timestamp = Date.now();
    setProfile(updatedProfile);
    notifyListeners(updatedProfile);
  }, []);

  // Effetto per il fetch automatico
  useEffect(() => {
    if (autoFetch) {
      fetchProfile();
    }
  }, [autoFetch, fetchProfile]);

  // Effetto per registrare il listener
  useEffect(() => {
    const listener = (newProfile) => {
      setProfile(newProfile);
    };
    
    profileListeners.add(listener);
    
    return () => {
      profileListeners.delete(listener);
    };
  }, []);

  // Se c'è un profilo cached valido, usalo
  useEffect(() => {
    if (isCacheValid() && profileCache.data && !profile) {
      setProfile(profileCache.data);
    }
  }, [isCacheValid, profile]);

  return {
    profile,
    loading: loading || profileCache.isLoading,
    error,
    fetchProfile,
    invalidateCache,
    updateProfile,
    isCacheValid: isCacheValid()
  };
};

// Hook per ottenere solo i dati del profilo senza gestione dello stato
export const useProfileData = () => {
  return {
    profile: profileCache.data,
    isValid: profileCache.data && profileCache.timestamp && Date.now() - profileCache.timestamp < CACHE_DURATION
  };
};

// Funzione per pre-caricare il profilo
export const preloadProfile = async (includeTitles = false) => {
  const tokenDetailsString = sessionStorage.getItem('userDetails');
  if (!tokenDetailsString) return null;
  
  const tokenDetails = JSON.parse(tokenDetailsString);
  const token = tokenDetails.idToken;
  
  if (!token) return null;
  
  // Se il cache è valido, non fare nulla
  if (profileCache.data && profileCache.timestamp && Date.now() - profileCache.timestamp < CACHE_DURATION) {
    return profileCache.data;
  }
  
  try {
    const apiClient = new MyApiClient();
    const promises = [apiClient.getProfile(token)];
    
    if (includeTitles) {
      promises.push(apiClient.getTitles(token));
    }
    
    const results = await Promise.all(promises);
    const profileData = results[0];
    
    if (includeTitles && results[1]) {
      profileData.club.titles = results[1];
    }
    
    profileCache.data = profileData;
    profileCache.timestamp = Date.now();
    
    notifyListeners(profileData);
    
    return profileData;
  } catch (error) {
    console.error('Errore nel pre-caricamento del profilo:', error);
    return null;
  }
};

export default useProfile;